import React, { createContext, useState, useEffect, useContext } from "react";
import { SavingsData, SavingEntry } from "../types";
import {
  loadSavingsData,
  addSavingEntry as addEntry,
  deleteSavingEntry as deleteEntry,
  updateSavingEntry as updateEntry,
} from "../utils/dataService";

interface SavingsContextType {
  data: SavingsData;
  loading: boolean;
  error: string | null;
  addEntry: (entry: Omit<SavingEntry, "id">) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  updateEntry: (
    id: string,
    updates: Partial<Omit<SavingEntry, "id">>
  ) => Promise<void>;
  refreshData: () => Promise<void>;
}

const SavingsContext = createContext<SavingsContextType | undefined>(undefined);

export const SavingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<SavingsData>({
    entries: [],
    lastUpdated: "",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refreshData = async () => {
    try {
      setLoading(true);
      const loadedData = await loadSavingsData();
      setData(loadedData);
      setError(null);
    } catch (err) {
      console.error("Failed to load savings data:", err);
      setError("Failed to load savings data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleAddEntry = async (entry: Omit<SavingEntry, "id">) => {
    try {
      await addEntry(entry);
      await refreshData();
    } catch (err) {
      console.error("Failed to add entry:", err);
      setError("Failed to add entry");
      throw err;
    }
  };

  const handleDeleteEntry = async (id: string) => {
    try {
      await deleteEntry(id);
      await refreshData();
    } catch (err) {
      console.error("Failed to delete entry:", err);
      setError("Failed to delete entry");
      throw err;
    }
  };

  const handleUpdateEntry = async (
    id: string,
    updates: Partial<Omit<SavingEntry, "id">>
  ) => {
    try {
      await updateEntry(id, updates);
      await refreshData();
    } catch (err) {
      console.error("Failed to update entry:", err);
      setError("Failed to update entry");
      throw err;
    }
  };

  return (
    <SavingsContext.Provider
      value={{
        data,
        loading,
        error,
        addEntry: handleAddEntry,
        deleteEntry: handleDeleteEntry,
        updateEntry: handleUpdateEntry,
        refreshData,
      }}
    >
      {children}
    </SavingsContext.Provider>
  );
};

export const useSavings = (): SavingsContextType => {
  const context = useContext(SavingsContext);
  if (context === undefined) {
    throw new Error("useSavings must be used within a SavingsProvider");
  }
  return context;
};
